import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { Lock, ArrowRight, X, Sparkles, QrCode, LayoutGrid, Wallet, CalendarCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PlanUpgradeContext = createContext({ openPlanLock: () => {}, openEventLimit: () => {} });

export function usePlanUpgrade() {
  return useContext(PlanUpgradeContext);
}

const DEFAULT_FEATURE = {
  title: "Recurso disponível no plano Plus",
  message:
    "Faça upgrade para o Plus e tenha acesso a Check-in com QR Code, Mesas & Assentos, Inspirações, Minhas Receitas e até 12 eventos ativos.",
};

const DEFAULT_LIMIT = {
  title: "Limite de eventos atingido",
  message:
    "O plano Essencial permite 1 evento ativo por vez. Faça upgrade para o Plus e gerencie até 12 eventos simultaneamente.",
};

export function PlanUpgradeProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(DEFAULT_FEATURE);
  const navigate = useNavigate();

  const openPlanLock = useCallback((opts) => {
    setContent({ ...DEFAULT_FEATURE, ...(opts || {}) });
    setOpen(true);
  }, []);

  const openEventLimit = useCallback((opts) => {
    setContent({ ...DEFAULT_LIMIT, ...(opts || {}) });
    setOpen(true);
  }, []);

  // Interceptor axios global dispara estes eventos em 403 plan_feature_locked / event_limit_reached
  useEffect(() => {
    const lockHandler = (ev) => openPlanLock(ev?.detail || {});
    const limitHandler = (ev) => openEventLimit(ev?.detail || {});
    window.addEventListener("liventy:plan-lock", lockHandler);
    window.addEventListener("liventy:event-limit", limitHandler);
    return () => {
      window.removeEventListener("liventy:plan-lock", lockHandler);
      window.removeEventListener("liventy:event-limit", limitHandler);
    };
  }, [openPlanLock, openEventLimit]);

  const close = () => setOpen(false);
  const goUpgrade = () => {
    setOpen(false);
    navigate("/upgrade");
  };

  return (
    <PlanUpgradeContext.Provider value={{ openPlanLock, openEventLimit }}>
      {children}
      {open && (
        <div
          className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center p-4 bg-black/55"
          style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
          onClick={close}
          data-testid="plan-upgrade-overlay"
        >
          <div
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-8"
            style={{ fontFamily: "'Poppins', sans-serif" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="plan-upgrade-modal"
          >
            <div className="flex items-start justify-between mb-2">
              <div
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10.5px] font-semibold text-white"
                style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" }}
              >
                <Lock size={11} /> Plano Plus
              </div>
              <button
                onClick={close}
                className="w-9 h-9 -mr-2 -mt-2 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500"
                data-testid="plan-upgrade-close"
                aria-label="Fechar"
              >
                <X size={16} />
              </button>
            </div>
            <h3 className="text-[22px] sm:text-[26px] font-semibold tracking-tight text-zinc-900 leading-tight mt-1">
              {content.title}
            </h3>
            <p className="text-[13.5px] text-zinc-500 mt-2 leading-relaxed">{content.message}</p>

            <div className="mt-5 grid grid-cols-1 gap-2 text-[12.5px] text-zinc-700">
              {[
                { icon: CalendarCheck, t: "Até 12 eventos ativos" },
                { icon: QrCode, t: "Check-in com QR Code" },
                { icon: LayoutGrid, t: "Mesas & Assentos + Mapa do evento" },
                { icon: Wallet, t: "Minhas Receitas e Inspirações" },
              ].map((it, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
                    <it.icon size={14} className="text-violet-500" />
                  </span>
                  <span>{it.t}</span>
                </div>
              ))}
            </div>

            <button
              onClick={goUpgrade}
              className="w-full mt-6 h-12 rounded-full text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
              style={{
                backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
                boxShadow: "0 12px 32px -10px rgba(107,61,246,0.45)",
              }}
              data-testid="plan-upgrade-cta"
            >
              Fazer Upgrade para Plus
              <ArrowRight size={15} />
            </button>
            <button
              onClick={close}
              className="w-full mt-2 h-10 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900"
              data-testid="plan-upgrade-dismiss"
            >
              Agora não
            </button>
          </div>
        </div>
      )}
    </PlanUpgradeContext.Provider>
  );
}
